import { useId } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';

import { useLlenado } from '#components/mobile/inicio/comunes';
import { calcularBodega } from '#components/mobile/inicio/dosNominas';
import type { NominaCuenta } from '#components/mobile/inicio/dosNominas';
import { estilos } from '#components/mobile/inicio/inicio.estilos';
import { color, espacio, texto } from '#components/mobile/ui/tokens';
import { InicialAutor } from '#components/pareja/InicialAutor';
import type { Tripulante } from '#components/pareja/tripulacionLogica';
import { PrivacyFilter } from '#components/PrivacyFilter';
import { useFormat } from '#hooks/useFormat';
import { usePayeesById } from '#hooks/usePayees';

type BodegaDosNominasProps = {
  nominas: readonly NominaCuenta[];
  /** De quién es cada nómina (id del pagador → id de la persona). */
  duenos: Record<string, string>;
  tripulantes: readonly Tripulante[];
  llega: number;
  necesita: number;
  /** Nombre del mes siguiente («noviembre»). */
  mes: string;
  onDueno: (payee: string, persona: string) => void;
};

/**
 * Cuenta común con dos nóminas: una bodega que se llena con lo que llega al
 * mes siguiente (cada nómina, una capa de su color) y la raya de lo asignado.
 * Debajo, cuánto pone cada uno. La primera vez pregunta de quién es cada
 * nómina.
 */
export function BodegaDosNominas({
  nominas,
  duenos,
  tripulantes,
  llega,
  necesita,
  mes,
  onDueno,
}: BodegaDosNominasProps) {
  const { t } = useTranslation();
  const format = useFormat();
  const { data: payees } = usePayeesById();
  const bodega = calcularBodega({ nominas, llega, necesita });

  const duenoDe = (payee: string) =>
    tripulantes.find(p => p.userId === duenos[payee]) ?? null;
  const nombreDe = (payee: string) => {
    const dueno = duenoDe(payee);
    if (dueno) {
      return dueno.esYo ? t('You') : dueno.nombre;
    }
    return payees?.[payee]?.name ?? t('(No payee)');
  };
  const colorDe = (payee: string) => {
    const dueno = duenoDe(payee);
    return dueno ? (dueno.esYo ? color.ok : color.accent) : color.fg3;
  };
  // Pregunta una sola vez por cada nómina sin dueño (si hay a quién elegir).
  const sinDueno =
    tripulantes.length > 1
      ? (nominas.find(n => !duenoDe(n.payee)) ?? null)
      : null;

  const cubierto = bodega.diferencia >= 0;
  const [a, b] = bodega.partes;

  return (
    <View style={{ gap: espacio.fila }} data-testid="cuenta-comun-bodega">
      <View style={{ flexDirection: 'row', gap: 14, alignItems: 'stretch' }}>
        <DibujoBodega
          capas={nominas.map(n => ({
            id: n.payee,
            valor: n.cobrado,
            color: colorDe(n.payee),
          }))}
          hay={bodega.hay}
          necesita={necesita}
          mes={mes}
        />
        <View style={{ flex: 1, minWidth: 0, gap: 6 }}>
          <Text style={estilos.etiquetaPequena}>
            <Trans>Salaries in the joint account</Trans>
          </Text>
          <PrivacyFilter>
            <Text
              data-testid="cuenta-comun-bodega-estado"
              style={{
                fontSize: 16,
                fontWeight: 800,
                color: cubierto ? color.ok : color.fg,
              }}
            >
              {cubierto
                ? t('{{month}} covered ✓', { month: mayuscula(mes) })
                : t('{{amount}} short', {
                    amount: format(-bodega.diferencia, 'financial'),
                  })}
            </Text>
            <Text style={estilos.filaSub}>
              {cubierto
                ? t('and {{amount}} left over to assign', {
                    amount: format(bodega.diferencia, 'financial'),
                  })
                : t('to reach {{amount}} assigned', {
                    amount: format(necesita, 'financial'),
                  })}
            </Text>
          </PrivacyFilter>
          {nominas.map(n => {
            const dueno = duenoDe(n.payee);
            const espera = n.cobrado <= 0;
            return (
              <View
                key={n.payee}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 8,
                  opacity: espera ? 0.55 : 1,
                }}
              >
                {dueno ? (
                  <InicialAutor tripulante={dueno} size={22} />
                ) : (
                  <View style={{ width: 22 }} />
                )}
                <Text
                  style={{
                    ...texto.pequeno,
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: color.fg,
                  }}
                >
                  {nombreDe(n.payee)}
                </Text>
                <PrivacyFilter>
                  <Text
                    style={{
                      ...texto.pequeno,
                      fontVariantNumeric: 'tabular-nums',
                      color: espera ? color.fg3 : color.fg,
                    }}
                  >
                    {espera
                      ? t('usually arrives on the {{day}}', { day: n.dia })
                      : format(n.cobrado, 'financial')}
                  </Text>
                </PrivacyFilter>
              </View>
            );
          })}
          {a && b && (
            <View style={{ gap: 4 }}>
              <View
                style={{
                  flexDirection: 'row',
                  height: 6,
                  borderRadius: 3,
                  overflow: 'hidden',
                  backgroundColor: color.surface3,
                }}
              >
                <View
                  style={{
                    width: `${a.porcentaje}%`,
                    backgroundColor: colorDe(a.payee),
                    transition: 'width 0.8s',
                  }}
                />
                <View
                  style={{
                    width: `${b.porcentaje}%`,
                    backgroundColor: colorDe(b.payee),
                    transition: 'width 0.8s',
                  }}
                />
              </View>
              <Text style={{ fontSize: 12, fontWeight: 600, color: color.fg2 }}>
                {bodega.esperando.length > 0
                  ? t('Waiting for the salary of {{name}}', {
                      name: nombreDe(bodega.esperando[0]),
                    })
                  : duenoDe(b.payee)?.esYo
                    ? t('You put in {{mine}} %, {{name}} {{theirs}} %', {
                        mine: b.porcentaje,
                        name: nombreDe(a.payee),
                        theirs: a.porcentaje,
                      })
                    : duenoDe(a.payee)?.esYo
                      ? t('You put in {{mine}} %, {{name}} {{theirs}} %', {
                          mine: a.porcentaje,
                          name: nombreDe(b.payee),
                          theirs: b.porcentaje,
                        })
                      : t('{{first}} {{p1}} %, {{second}} {{p2}} %', {
                          first: nombreDe(a.payee),
                          p1: a.porcentaje,
                          second: nombreDe(b.payee),
                          p2: b.porcentaje,
                        })}
              </Text>
            </View>
          )}
        </View>
      </View>

      {sinDueno && (
        <View
          data-testid="cuenta-comun-de-quien"
          style={{
            gap: 8,
            padding: '10px 12px',
            borderRadius: 12,
            backgroundColor: color.surface2,
          }}
        >
          <Text style={{ fontSize: 13, fontWeight: 700, color: color.fg }}>
            {t('Whose salary is «{{payee}}»?', {
              payee: payees?.[sinDueno.payee]?.name ?? '',
            })}
          </Text>
          <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
            {tripulantes.map(p => (
              <Button
                key={p.userId}
                variant="bare"
                onPress={() => onDueno(sinDueno.payee, p.userId)}
                style={{
                  gap: 6,
                  minHeight: 40,
                  padding: '6px 12px',
                  borderRadius: 999,
                  border: `1px solid ${color.line}`,
                  backgroundColor: color.surface,
                }}
              >
                <InicialAutor tripulante={p} size={20} />
                <Text style={{ fontSize: 13, fontWeight: 700 }}>
                  {p.esYo ? t('Mine') : p.nombre}
                </Text>
              </Button>
            ))}
          </View>
        </View>
      )}
    </View>
  );
}

const ALTO = 112;
const BASE = 124;

/** La bodega en SVG: capas de abajo arriba y la raya de lo asignado. */
function DibujoBodega({
  capas,
  hay,
  necesita,
  mes,
}: {
  capas: { id: string; valor: number; color: string }[];
  hay: number;
  necesita: number;
  mes: string;
}) {
  const clip = useId();
  // La bodega llena es un 15 % más que lo mayor, para que la raya se vea.
  const capacidad = Math.max(hay, necesita, 1) * 1.15;
  const altura = (valor: number) => (valor / capacidad) * ALTO;
  const llenado = useLlenado(1);

  // Lo cobrado no puede pasar de lo que hay (si ya se gastó algo).
  const cobrado = capas.reduce((s, c) => s + c.valor, 0);
  const escala = cobrado > hay && cobrado > 0 ? hay / cobrado : 1;
  const base = Math.max(0, hay - cobrado * escala);
  const pilas = [
    { id: 'base', valor: base, color: color.fg4 },
    ...capas.map(c => ({ ...c, valor: c.valor * escala })),
  ];
  let y = BASE;
  const rects = pilas.map(p => {
    const h = altura(p.valor) * llenado;
    y -= h;
    return { ...p, y, h };
  });
  const yRaya = BASE - altura(necesita);

  return (
    <svg
      viewBox="0 0 110 132"
      width={96}
      height={116}
      aria-hidden
      style={{ flexShrink: 0, overflow: 'visible' }}
    >
      <defs>
        <clipPath id={clip}>
          <path d="M8 10 H102 L94 122 Q55 130 16 122 Z" />
        </clipPath>
      </defs>
      <path
        d="M8 10 H102 L94 122 Q55 130 16 122 Z"
        fill={color.surface2}
        stroke={color.line2}
        strokeWidth={2}
      />
      <g clipPath={`url(#${clip})`}>
        {rects.map(r => (
          <rect
            key={r.id}
            x={0}
            width={110}
            y={r.y}
            height={r.h + (r.id === 'base' ? 10 : 0)}
            fill={r.color}
            opacity={0.8}
            style={{ transition: 'y 1.4s ease-out, height 1.4s ease-out' }}
          />
        ))}
        {[34, 58, 82, 106].map(yTabla => (
          <line
            key={yTabla}
            x1={0}
            x2={110}
            y1={yTabla}
            y2={yTabla}
            stroke={color.line2}
            strokeWidth={1}
            opacity={0.7}
          />
        ))}
      </g>
      {necesita > 0 && (
        <>
          <line
            x1={2}
            x2={108}
            y1={yRaya}
            y2={yRaya}
            stroke={color.fg}
            strokeWidth={1.6}
            strokeDasharray="4 3"
          />
          <text
            x={55}
            y={yRaya - 4}
            textAnchor="middle"
            style={{ fontSize: 9.5, fontWeight: 800, fill: color.fg2 }}
          >
            {mes}
          </text>
        </>
      )}
    </svg>
  );
}

function mayuscula(texto: string) {
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}
